<template>
    <div>
        <b-sidebar
            id="sidebar-task-handler"
            sidebar-class="sidebar-lg"
            :visible="isFeeHandlerSidebarActive"
            bg-variant="white"
            shadow
            backdrop 
            no-header
            right
            @change="changeHandler($event)"
            @hidden="clearForm"
        >
            <template #default="{ hide }">
                <!-- Header -->
                <div
                    class="
                        d-flex
                        justify-content-between
                        align-items-center
                        content-sidebar-header
                        px-2
                        py-1
                        side-bar-header
                    "
                >
                    <h3 class="mb-0">
                        {{ feeSideBarTitle }}
                    </h3>

                    <div>
                        <feather-icon
                            class="ml-1 cursor-pointer"
                            icon="XIcon"
                            size="16"
                            @click="hide"
                        />
                    </div>
                </div>

                <!-- Body -->
                <validation-observer
                    v-slot="{ handleSubmit }"
                    ref="refFormObserver"
                >
                    <!-- Form -->
                    <b-form
                        class="p-2"
                        @submit.prevent="handleSubmit(onSubmit)"
                        @reset.prevent="resetForm"
                    >
                        <!-- Title -->
                        <validation-provider
                            v-slot="validationContext"
                            name="name"
                            rules="required"
                        >
                            <b-form-group label="Name" label-for="name">
                                <b-form-input
                                    id="name"
                                    v-model="feeLocal.name"
                                    data-test ="fee-name"
                                    autofocus
                                    :state="
                                        getValidationState(validationContext)
                                    "
                                    trim
                                    placeholder="Enter fee name"  
                                    :disabled ="isFeeView"
                                />

                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        
                        <validation-provider
                            v-slot="{ errors }"
                            name="amount"
                            rules="required"
                        >
                            <b-form-group label="Amount">
                                <b-form-input
                                    v-model="feeLocal.amount"
                                    v-mask="amountMask"
                                    placeholder="Enter fee amount"
                                    :state="errors.length > 0 ? false : null"
                                    data-test="fee-amount"
                                    type="text"
                                    :disabled ="isFeeView"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-if="! feeLocal.id"
                            v-slot="validationContext"
                            name="duration"
                            rules="required"
                        >
                            <b-form-group label="Duration" label-for="duration">
                                <b-form-input
                                    id="duration"
                                    v-model="feeLocal.duration"
                                    data-test ="fee-duration"
                                    autofocus
                                    :state="
                                        getValidationState(validationContext)
                                    "
                                    trim
                                    placeholder="Enter fee duration"
                                />

                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-if="! feeLocal.id"
                            v-slot="{ errors }"
                            name="service"
                            rules="required"
                        >
                            <b-form-group label="Service" :state="errors.length > 0 ? false : null">
                                <v-select
                                    ref="feeService"
                                    v-model="feeLocal.service"
                                    class="custom-select-icon"
                                    :clearable="false"
                                    placeholder="Select fee service"
                                    label="name"
                                    :options="serviceOptions"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-if="! feeLocal.id"
                            v-slot="{ errors }"
                            name="nature"
                        >
                            <b-form-group label="Nature">
                                <v-select
                                    ref="feeNature"
                                    v-model="feeLocal.nature"
                                    class="custom-select-icon"
                                    :clearable="false"
                                    placeholder="Select fee nature"
                                    label="name"
                                    :options="natureOptions"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>
                        
                        <!-- Form Actions -->
                        <div class="d-flex">
                            <b-button
                                v-if="!isFeeView"
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                variant="primary"
                                class="
                                    mr-1
                                    mt-1
                                    d-flex
                                    justify-content-center
                                    align-items-center
                                "
                                :disabled="dataSidebarActionLoading"
                                type="submit"
                                data-test="create-fee-btn"
                                >
                                 <span  :class="dataSidebarActionLoading ? 'mr-2' : ''">
                                    {{ feeLocal.id ? "Update" : "Add " }}
                                 </span>

                                <img
                                    v-if="!feeLocal.id && !dataSidebarActionLoading"
                                    :src="'/illustrations/add-circle.svg'"
                                    class="ml-1"
                                    alt="add-logo"
                                />

                                <img
                                    v-if="feeLocal.id && !dataSidebarActionLoading"
                                    :src="'/illustrations/edit.svg'"
                                    class="ml-1"
                                    alt="edit-logo"
                                />
                                <normal-loader v-if="dataSidebarActionLoading" class="action-button left-10%" />
                            </b-button>

                            <b-button
                                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                                class="
                                mt-1 
                                d-flex
                                justify-content-center
                                align-items-center
                                "
                                variant="outline-primary"
                                @click="hideSideBar"
                                >Cancel
                                <vector 
                                    data-type="icons" 
                                    data-width="15px" 
                                    data-height="15px" 
                                    data-name="cancel" 
                                    class="float-right ml-1"
                                    alt="cancel-logo"
                                />
                            </b-button>
                        </div>
                    </b-form>
                </validation-observer>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
import {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BFormInvalidFeedback,
} from "bootstrap-vue";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import { toRefs } from "@vue/composition-api";
import Fee from '@/models/Fee';
import useFeeHandler from "./useFeeHandler";
import { amountMask } from '@/helpers';

export default {
    components: {
        // BSV
        BButton,
        BSidebar,
        BForm,
        BFormGroup,
        BFormInput,
        BFormInvalidFeedback,
        vSelect,

        // Form Validation
        ValidationProvider,
        ValidationObserver,
    },

    directives: {
        Ripple,
    },

    model: {
        prop: "isFeeHandlerSidebarActive",
        event: "update:is-fee-handler-sidebar-active",
    },

    props: {
        isFeeHandlerSidebarActive: {
            type: Boolean,
            required: true,
        },

        fee: {
            type: Object,
            required: true,
        },

        clearFeeData: {
            type: Function,
            required: true,
        },

        isFeeView : {
            type: Boolean,
            default: false,
            required: false
        },

        dataSidebarActionLoading: {
            type: Boolean,
            default: false,
        },
    },

    setup(props, { emit }) {
        const {
            feeLocal,

            // UI
            onSubmit,
        } = useFeeHandler(toRefs(props), emit); 

        if (feeLocal.value.nature)
            // eslint-disable-next-line prefer-destructuring
            feeLocal.value.nature = props.natureOptions.filter(
                opt => opt.value === feeLocal.value.nature
            )[0];

        const { refFormObserver, getValidationState, resetForm, clearForm } = formValidation(feeLocal, props.clearTaskData);
       
        return {
            // Add New
            feeLocal,
            onSubmit,

            // Form Validation
            resetForm,
            clearForm,
            refFormObserver,
            getValidationState,
        };
    },

    data() {
        return {
            required,
            natureOptions: [],
            serviceOptions: [],

            amountMask,
        };
    },

    computed: {
        feeSideBarTitle(){
            if(this.feeLocal.id && this.isFeeView)
                return "Fee";  
                  
            if(this.feeLocal.id && ! this.isFeeView)
                return "Update Fee";

            return "Add Fee";
        },
    },
    
    async created() {
        [this.serviceOptions, this.natureOptions] = await Fee.getNatureAndServiceOptions();
        this.natureOptions.unshift({
            id: 0,
            name: '-'
        });
    },

    methods: {
        hideSideBar() {
            this.$emit('update:is-fee-handler-sidebar-active', false);
            this.$emit('cancelView');
        },

        changeHandler(value) {
            this.$emit('update:is-fee-handler-sidebar-active', value);
            if(value === false){
                this.$emit('cancelView');
            }
        },
    },
};
</script>

<style scope>
.side-bar-header{
   background: #f6f6f6 !important;
 }
 
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>